import type { TimelineItem } from './session.types';
import type { ChatMessage } from '@/types/chat';
import type { AgentUiActionId } from '@hector/shared-types';

/**
 * Lifecycle phase of an exchange, used by StreamingTimeline to determine
 * what UI elements to show (spinners, typing dots, action buttons).
 *
 * - history:        Completed exchange (from DB or archived in memory). No animations.
 * - live_pending:   User just sent a message, waiting for first backend event. Show spinner.
 * - live_streaming: Agent is writing / using tools. Show typing dots between events.
 * - live_done:      Agent finished (agent_done received). Show copy/save buttons.
 */
export type ExchangePhase = 'history' | 'live_pending' | 'live_streaming' | 'live_done';

/**
 * Normalized exchange ready for rendering. One user message + the full timeline
 * of the assistant's response (content segments, tool calls, JP items, etc.).
 *
 * Produced by the display-exchange helpers from DB history, WebSocket live
 * state, and optimistic pending prompts. The final render list is selected by
 * the same pipeline so restored, live, and pending turns share one code path.
 */
export interface DisplayExchange {
  id: string;
  userMessage: ChatMessage | null;
  timeline: TimelineItem[];
  phase: ExchangePhase;
  /**
   * Dossier selected by the human for this exact user turn.
   *
   * `undefined` is deliberately distinct from `null`: persisted history created
   * before this client-side snapshot did not carry a per-turn dossier and may use
   * the current conversation fallback. `null` means this particular turn was
   * intentionally sent without a dossier and must not borrow a later selection.
   */
  caseId?: string | null;
  /**
   * Host capabilities cloned when this local turn was submitted. This is
   * deliberately session-only: UI actions are not persisted in DB history.
   */
  uiActionCapabilities?: readonly AgentUiActionId[];
  /**
   * Real `casediscovery_messages_v2.id` of the LAST assistant turn of this
   * exchange — the anchor for the feedback thumbs. Populated on historical
   * exchanges from REST and, for local-first committed turns, as soon as the
   * terminal `agent_done` snapshot carries the backend message id. Undefined on
   * a still-live exchange before that terminal local snapshot arrives.
   */
  lastAssistantMessageId?: string;
}

/** Resolve an exchange-local dossier without changing old persisted transcripts. */
export function resolveDisplayExchangeCaseId(
  exchange: DisplayExchange,
  legacySelectedCaseId?: string | null,
): string | undefined {
  if (exchange.caseId === undefined) return legacySelectedCaseId ?? undefined;
  return exchange.caseId ?? undefined;
}
